import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { Task } from '../api';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { formatPercentageNumber, InvestmentSplitWithAnswers } from './utils';
import { removeDollarSign } from 'shared/design-system/components/TrueImpactDollarField';
import { mergeClassNames } from 'shared/utils/mergeClassNames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faEye,
  faEyeSlash,
} from '@fortawesome/pro-light-svg-icons';

const StyledDonorButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;
  cursor: pointer;
  padding-top: ${SPACERS.extraTiny};
  padding-bottom: ${SPACERS.extraTiny};
  svg {
    width: 16px;
    height: 16px;
    margin-right: 6px;
  }
`;

export const ViewDonorDetailsButton: React.FC<{
  className?: string;
  donorDetailsOpen: boolean;
  setDonorDetailsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ className, donorDetailsOpen, setDonorDetailsOpen }) => {
  return (
    <StyledDonorButton
      className={className}
      onClick={() => setDonorDetailsOpen(o => !o)}
    >
      <FontAwesomeIcon
        icon={donorDetailsOpen ? faEyeSlash : faEye}
        color={COLORS.ctaPrimary}
      />
      <TrueImpactText variant="body-small" color="ctaPrimary">
        {donorDetailsOpen ? 'Hide' : 'View'} donor details
      </TrueImpactText>
    </StyledDonorButton>
  );
};

interface AllocationSplitProps {
  className?: string;
  task: Task;
  donorDetailsOpen: boolean;
  finishedIndexes: Set<number>;
  totalAmount: string | undefined;
  investmentFinalSplitIndex: number;
  programNameByDonor: string | undefined;
  investmentFinalSplit: Required<InvestmentSplitWithAnswers>[];
  handleInvestmentIndexChange: (index: number) => unknown;
  setDonorDetailsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const StyledDiv = styled.div`
  width: 217px;
  background-color: ${COLORS.grayLight};
  .investment-space {
    padding: ${SPACERS.small} ${SPACERS.medium};
  }
  .heading-details,
  .heading-details-sub {
    margin-bottom: 0;
  }
  .heading-details-sub {
    border-bottom: 1px solid ${COLORS.grayBase};
  }
  .investment-row {
    cursor: pointer;
    position: relative;
    .investment-done-check {
      position: absolute;
      transform: translate(-50%, -50%);
      top: 50%;
      left: 50%;
    }
  }
  .investment-row.finished {
    background-color: ${COLORS.successBackground} !important;
  }
  .investment-row.finished.selected {
    background-color: #deffec !important;
  }
  .investment-row.selected {
    background-color: ${COLORS.grayLightest};
  }
  .investment-row.bordered {
    border-bottom: 1px solid ${COLORS.grayBase};
  }
  .investment-year-details {
    display: flex;
    justify-content: space-between;
    margin-top: ${SPACERS.small};
    margin-bottom: ${SPACERS.small};
  }
  .allocation-split-button-wrapper {
    padding-left: ${SPACERS.medium};
    padding-right: ${SPACERS.medium};
    .allocation-split-button {
      margin-top: ${SPACERS.small};
    }
  }
`;

const AllocationSplit: React.FC<AllocationSplitProps> = ({
  investmentFinalSplit,
  totalAmount,
  finishedIndexes,
  donorDetailsOpen,
  setDonorDetailsOpen,
  investmentFinalSplitIndex,
  className,
  handleInvestmentIndexChange,
}) => {
  const lastIndex = investmentFinalSplit.length - 1;
  return (
    <StyledDiv className={className}>
      <div className="allocation-split-button-wrapper">
        <ViewDonorDetailsButton
          donorDetailsOpen={donorDetailsOpen}
          setDonorDetailsOpen={setDonorDetailsOpen}
          className="allocation-split-button"
        />
      </div>
      <TrueImpactText
        variant="heading-subheading"
        className="heading-details-sub investment-space"
      >
        Allocations
      </TrueImpactText>
      {investmentFinalSplit.map((i, index) => {
        const isFinished = finishedIndexes.has(index);
        return (
          <div
            key={index}
            onClick={() => handleInvestmentIndexChange(index)}
            className={mergeClassNames(
              'investment-row',
              'investment-space',
              isFinished && 'finished',
              lastIndex !== index && 'bordered',
              index === investmentFinalSplitIndex && 'selected',
            )}
          >
            {isFinished && (
              <FontAwesomeIcon
                icon={faCircleCheck}
                size="4x"
                color={COLORS.success}
                className="investment-done-check"
              />
            )}
            <TrueImpactText
              color="blackBase"
              variant="heading-label"
              className="heading-details"
            >
              PROGRAM:
            </TrueImpactText>
            <TrueImpactText>{i.program.name}</TrueImpactText>
            <div className="investment-year-details">
              <div>
                <TrueImpactText
                  color="blackBase"
                  variant="heading-label"
                  className="heading-details"
                >
                  YEAR:
                </TrueImpactText>
                <TrueImpactText>{i.year}</TrueImpactText>
              </div>
              <div>
                <TrueImpactText
                  color="blackBase"
                  variant="heading-label"
                  className="heading-details"
                >
                  AMOUNT:
                </TrueImpactText>
                <TrueImpactText>${numberWithCommas(i.amount)}</TrueImpactText>
              </div>
            </div>
            <TrueImpactText
              color="blackBase"
              variant="heading-label"
              className="heading-details"
            >
              PERCENTAGE:
            </TrueImpactText>
            <TrueImpactText>
              {formatPercentageNumber(
                (Number(i.amount) / Number(removeDollarSign(totalAmount))) *
                  100 || 0,
              )}
              %
            </TrueImpactText>
          </div>
        );
      })}
    </StyledDiv>
  );
};

export default AllocationSplit;
